<template>
  <section>
    <div class="content-wrapper">

      <errors v-if="isVisibleErrorsModal"></errors>

      <question v-if="display"></question>

      <address-suggestion v-if="isSuggestedAddressModalVisible"></address-suggestion>

      <bank-account-changed-warning
        :isVisible="isBankAccountChangedWarningVisible"
        :isAdminView="true"
        @close="hideBankAccountChangedWarning"
        @goToUpcomingTransactions="handleGoToUpcomingTransactions" />

      <div class="content-heading">
        Institution account
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <breadcrumb class="breadcrumb" v-bind:breadcrumbsData="breadcrumbData" />
      <br>

      <spinner v-if="userDetailLoading
        || accountUsersLoading
        || isNotesSegmentLoading
        || isFundingSourcesSegmentLoading
        || preferencesSpinner
        || isUpcomingTransactionsSegmentLoading
        || isAdditionalEmailsLoading
        || isRepresentativesLoading"></spinner>

      <div class="row">
        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
            <div class="panel-title top-margin">Account information</div>

            <div class="line-separator"></div>

            <div class="row">
              <div class="col-md-6">
                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Institution name *</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <RestrictedSingleInput
                      @inputUpdated="onInstitutionNameUpdate"
                      :input="organization.name"
                      :limit="40"
                    />
                    <span
                      v-if="organization && organization.name && organization.name.trim().length === 0"
                      class="error-feedback"
                    >Required</span>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>ID</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <p> {{account.accountId}} </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                  <div class="row space zero-margin-x">
                    <div>
                      <label>Date Created:</label>
                    </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <p> {{formatDate(account.createdAt)}} </p>
                    </div>
                  </div>
                </div>

                <div class="row space zero-margin-x" v-if="account.investorSince">
                  <div class="col-12">
                    <div>
                      <label>Investor since:</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x" v-if="account.investorSince">
                  <div class="col-12">
                    <div>
                      <p> {{account.investorSince}} </p>
                    </div>
                  </div>
                </div>
              </div>
              </div>

              <div class="col-md-6">
                <div class="row space zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Status:</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <p v-if="account.status == 'active'" class="label label-success capitalize-text"> {{account.status}} </p>
                      <p v-if="account.status == 'pending'" class="label label-warning capitalize-text"> {{account.status}} </p>
                      <p v-if="account.status == 'inactive'" class="label label-danger capitalize-text"> {{account.status}} </p>
                      <p v-if="account.hasUserComments" class="label label-danger capitalize-text"> Comments </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="row space zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Institution type *</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div class="">
                      <select v-model="organization.type"
                        name="institutionType"
                        id="institutionType" required
                        :class="{ 'form-control': true, 'form-control-error': errors.has('institutionType') }" v-validate="{required: true}">

                        <option v-for="(institutionType, index) in institutionTypes"
                          :key="index"
                          :value="institutionType.name">
                          {{institutionType.text}}
                        </option>

                      </select>
                      <span v-if="errors.has('institutionType')" class="error-feedback">Required</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="row space zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Tax ID *</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <input name="taxId" id="taxId" :class="{ 'form-control': true,
                    'form-control-error': errors.has('taxId') || !isTaxIdValid() }" v-validate="{required: true}"
                    v-model="organization.taxId" v-mask="'99-9999999'" />
                    <span v-if="errors.has('taxId') || !isTaxIdValid()" class="error-feedback">Required</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="row space zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Phone number *</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <input name="phone" id="phone" :class="{ 'form-control': true,
                    'form-control-error': errors.has('phone') || !isPhoneNumberValid() }" v-mask="'(999) 999-9999'"
                    v-validate="{required: true}" v-model="organization.phoneNumber" />
                    <span v-if="errors.has('phone') || !isPhoneNumberValid()" class="error-feedback">Required</span>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <!--  -->
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="row space zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Legacy Username:</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <p> {{account.legacyUsername}} </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="row space zero-margin-x">
                  <div class="col-12">
                    <div>
                      <label>Legacy Customer Number:</label>
                    </div>
                  </div>
                </div>

                <div class="row zero-margin-x">
                  <div class="col-12">
                    <div>
                      <p> {{account.legacyCustomerNumber}} </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Addresses
              v-if="organization.mainAddress && organization.mailingAddress"
              v-bind:firstTitle="'Institution Address'"
              v-bind:residentialAddress="organization.mainAddress"
              v-bind:secondTitle="'Mailing Address'"
              v-bind:mailingAddress="organization.mailingAddress"
              v-bind:key="1"
              v-bind:is-admin="true"
              ref="addresses" />

            <div class="row zero-margin-x">
                <div class="col-12" >
                <br/>
                  <button class="btn btn-default btn-lg"
                    @click="validateAndSubmitAccount()"
                    :disabled="shouldSubmitButtonBeDisabled()">
                    Submit account information
                  </button>
                </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="accountRepresentatives !== undefined">
        <div class="col-lg-12">
          <AccountEmails
            :accountId="accountId"
            :emails="accountEmails"
            @additionalEmailsAddingFailed="setAdditionalEmailSpinnerOff"
            @additionalEmailsAddingStarted="setAdditionalEmailSpinnerOn"
            @additionalEmailsAddingSucceed="setAdditionalEmailSpinnerOff"
            @additionalEmailsDeletingFailed="setAdditionalEmailSpinnerOff"
            @additionalEmailsDeletingStarted="setAdditionalEmailSpinnerOn"
            @additionalEmailsDeletingSucceed="setAdditionalEmailSpinnerOff" />
        </div>
      </div>

      <div class="row" v-if="accountRepresentatives !== undefined && account.primaryInvestorId">
        <div class="col-lg-12">
          <AccountRepresentatives
            :accountId="accountId"
            :accountName="accountName"
            :accountType="getAccountType()"
            :representatives="accountRepresentatives"
            :primaryInvestorId="account.primaryInvestorId"
            @representativesLoadingStarted="setRepresentativesSpinnerOn"
            @representativesLoadingSucceed="setRepresentativesSpinnerOff"
            @representativesLoadingFailed="setRepresentativesSpinnerOff"
            @representativesUpdated="loadAccountInformation"/>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-12">
          <AccountNotes
            :accountId="accountId"
            :displayAlertForUpcomingTransactionsWithAction="shouldUpcomingTransactionsWithActionsBeDisplayed"
            :displayAlertForUpcomingTransactionsWithNoAction="shouldUpcomingTransactionsWithNoActionsBeDisplayed"
            :disable-investment="user.disableInvestment || organization.disableInvestment"
            @goToUpcomingTransactionsSegment="goToSegment('upcoming-transactions-segment')"
            @notesLoadingFailed="setNotesSpinnerOff"
            @notesLoadingStarted="setNotesSpinnerOn"
            @notesLoadingSucceed="setNotesSpinnerOff"
            @redirectToNoteDetailsView="goToViewDetail"
            @redirectToPurchaseInvestmentNoteView="goToPurchaseInvestmentNote" />
        </div>
      </div>

      <div id="funding-sources-segment" class="row" v-if="account.defaultFundingSourceId !== undefined">
        <div class="col-lg-12">
          <AccountFundingSources
            :accountId="accountId"
            :defaultFundingSourceId="userAccount.defaultFundingSourceId"
            :account="account"
            @fundingSourcesLoadingFailed="setFundingSourceSpinnerOff"
            @fundingSourcesLoadingStarted="setFundingSourceSpinnerOn"
            @fundingSourcesLoadingSucceed="setFundingSourceSpinnerOff"
            @defaultFundingSourceUpdated="handleDefaultFundingSourceUpdated"
            @restart="loadAccountUsers"
            @showBankAccountChangedWarning="showBankAccountChangedWarning" />
        </div>
      </div>

      <div class="row">
        <div class="col-lg-12">
          <AdminAccountDocuments />
        </div>
      </div>

      <div class="row" id="preferences-segment">
        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
            <div class="panel-title">
              Preferences
            </div>

            <SuccessAlert
              :isVisible="isPreferencesSuccessMessageVisible"
              :message="preferencesSuccessMessage"
              @hideSuccessAlert="hidePreferencesSuccessMessage" />

            <div class="line-separator"></div>

            <AccountPreferences
              v-if="account.communicationPreference !== undefined"
              :accountId="accountId"
              :interestPreference="account.interestPreference"
              :communicationPreference="account.communicationPreference"
              :communication1099Preference="account.communication1099Preference"
              :communicationImpactReportPreference="account.communicationImpactReportPreference"
              :paymentPreference="account.paymentPreference"
              :redemptionRestriction="account.redemptionRestriction"
              :user="user"
              @impactPreferencesLoadingFailed="setPreferencesSpinnerOff"
              @impactPreferencesLoadingStarted="setPreferencesSpinnerOn"
              @impactPreferencesLoadingSucceed="setPreferencesSpinnerOff"
              @updateImpactPreferencesFailed="setPreferencesSpinnerOff"
              @updateImpactPreferencesStarted="setPreferencesSpinnerOn"
              @updateImpactPreferencesSucceed="displayImpactPreferenceSuccessfulNotification" />
          </div>
        </div>
      </div>

      <div id="upcoming-transactions-segment" class="row">
        <div class="col-lg-12">
          <UpcomingTransactions
            ref="upcomingTransactions"
            :accountId="accountId"
            :defaultFundingSourceId="userAccount.defaultFundingSourceId"
            :investorAccountIdentifier="userAccount.accountId"
            @goToFundingSourcesSegment="goToSegment('funding-sources-segment')"
            @hideAlertForUpcomingTransactions="hideUpcomingTransactionsAlerts"
            @showAlertForUpcomingTransactionsWithAction="displayUpcomingTransactionsWithActionAlert"
            @showAlertForUpcomingTransactionsWithNoAction="displayUpcomingTransactionsWithNoActionAlert"
            @upcomingTransactionsLoadingFailed="setUpcomingTransactionsSpinnerOff"
            @upcomingTransactionsLoadingStarted="setUpcomingTransactionsSpinnerOn"
            @upcomingTransactionsLoadingSucceed="setUpcomingTransactionsSpinnerOff"
            />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import {
    mapActions,
    mapMutations,
    mapState,
  } from 'vuex';

  import _ from 'lodash';
  import AccountEmails from '@/components/AccountEmails';
  import AccountFundingSources from '@/components/AccountFundingSources';
  import AccountNotes from '@/components/AccountNotes';
  import AccountPreferences from '@/components/AccountPreferences';
  import AccountRepresentatives from '@/components/AccountRepresentatives';
  import Errors from '@/components/modals/Errors';
  import Question from '@/components/modals/Question';
  import BankAccountChangedWarning from '@/components/modals/BankAccountChangedWarning';
  import SuccessAlert from '@/components/SuccessAlert';
  import UpcomingTransactions from '@/components/UpcomingTransactions';
  import Addresses from '@/components/Addresses';
  import AddressSuggestion from '@/components/modals/AddressSuggestion';
  import breadcrumb from '@/components/Breadcrumb';
  import AdminAccountDocuments from '@/components/AdminAccountDocuments';
  import { UserTypes } from '@/data/static/userTypes';
  import RestrictedSingleInput from '@/components/formControls/RestrictedSingleInput';

  import {
    HIDE_QUESTION_MODAL,
    SET_CREATION_SUCCEEDED,
    SET_ERRORS_MODAL_INFO,
    SHOW_CHECK_WIRE_MODAL,
    SHOW_ERRORS_MODAL,
    SHOW_QUESTION_MODAL,
    SET_SUGGESTED_ADDRESS,
    SET_SUGGESTED_ADDRESS_TYPE,
    SET_SUGGESTED_ADDRESS_MODAL_VISIBLE,
    SET_SUGGESTED_ADDRESS_YES_METHOD,
  } from '@/store/mutation-types';

  import {
    findAccount,
    formatAmount,
    formatDate,
    formatPaymentPreference,
    formatRate,
    formatTerm,
    formatType,
    isAddressValid,
    isStringValid,
    camelToSnakeCase,
  } from '@/util';
  import spinner from '@/components/widgets/Spinner';


  import {
    Constants,
  } from '../data/static/constants';

  const mix = {
    methods: {
      formatDate,
      formatAmount,
      formatRate,
      formatTerm,
      formatType,
      formatPaymentPreference,
      findAccount,
    },
  };

  export default {
    name: 'editInstitutionAccount',
    beforeDestroy() {
      this.hidePreferencesSuccessMessage();
    },
    beforeMount() {
      this.setParams();
      this.loadUserDetail();
      this.loadAccountUsers();
    },
    mixins: [mix],
    components: {
      AccountEmails,
      AccountFundingSources,
      AccountNotes,
      AccountPreferences,
      AccountRepresentatives,
      Addresses,
      AddressSuggestion,
      errors: Errors,
      question: Question,
      BankAccountChangedWarning,
      spinner,
      SuccessAlert,
      UpcomingTransactions,
      breadcrumb,
      AdminAccountDocuments,
      RestrictedSingleInput,
    },
    watch: {
      async suggestedAddressModalYesCall({ payload, suggestedAddress, addressType }) {
        if (!this.$refs.addresses.isMailingAddressDifferent) {
          this.organization.mainAddress = suggestedAddress;
          this.organization.mailingAddress = suggestedAddress;
          this.$refs.addresses.setResidencialAddress(suggestedAddress);
          this.$refs.addresses.setMailingAddress(suggestedAddress);
        }
        if (['organization'].includes(addressType)) {
          this.organization.mainAddress = suggestedAddress;
          this.$refs.addresses.setResidencialAddress(suggestedAddress);
        } else if (addressType === 'mailing') {
          this.organization.mailingAddress = suggestedAddress;
          this.$refs.addresses.setMailingAddress(suggestedAddress);
        }
        await this.save({
          ...payload,
          mailingAddress: this.organization.mailingAddress,
          mainAddress: this.organization.mainAddress,
        });
      },
      user: function changedUser(newUser) {
        this.setBreadcrumbData(this.account, newUser);
      },
      account: function changedAccount(newAccount) {
        this.setBreadcrumbData(newAccount, this.user);
      },
    },
    data() {
      return {
        accountId: '',
        account: {},
        accountUsersLoading: false,
        breadcrumbData: [],
        currentAmountBalance: 0,
        fundingSources: [],
        isAdditionalEmailsLoading: false,
        isBankAccountChangedWarningVisible: false,
        isFundingSourcesSegmentLoading: false,
        isNotesSegmentLoading: false,
        isPreferencesSuccessMessageVisible: false,
        isUpcomingTransactionsSegmentLoading: false,
        isRepresentativesLoading: false,
        maxAmountFundingSources: 6,
        name: '',
        noteLoading: false,
        notes: [],
        notesError: '',
        organization: {
          name: '',
        },
        preferencesSpinner: false,
        preferencesSuccessMessage: '',
        previousFundingSourceId: '',
        previousPaymentPreference: '',
        shouldUpcomingTransactionsWithActionsBeDisplayed: false,
        shouldUpcomingTransactionsWithNoActionsBeDisplayed: false,
        user: [],
        userAccount: { defaultFundingSourceId: '' },
        userDetailLoading: false,
        userId: '',
        accountRepresentatives: [],
        accountEmails: [],
      };
    },
    computed: {
      ...mapState('accountCreation', [
        'isSuggestedAddressModalVisible',
        'suggestedAddressModalYesCall',
      ]),
      ...mapState('app', [
        'institutionTypes',
      ]),
      ...mapState('errors', [
        'isVisibleErrorsModal',
      ]),
      ...mapState('home', [
        'userStatus',
      ]),
      ...mapState('question', [
        'display',
      ]),
      ...mapState('checkWireModal', [
        'isCheckWireModalVisible',
        'creationSucceeded',
      ]),
      accountName() {
        return this.account.accountName;
      },
      accountType() {
        return this.account.accountType;
      },
    },
    methods: {
      ...mapActions('account', [
        'getAccount',
        'getAccountUsers',
        'getAccountEmails',
        'getAccountOrganizations',
        'getAccountNotes',
        'getAccountFundingSources',
        'updateAccountPaymentPreference',
        'setDefaultFundingSource',
        'editInstitutionAccountInformation',
      ]),
      ...mapActions('accountCreation', [
        'getUserDetail',
        'verifyOrganizationAddress',
        'verifyMailingAddress',
      ]),
      ...mapMutations('accountCreation', {
        setSuggestedAddress: SET_SUGGESTED_ADDRESS,
        setSuggestedAddressType: SET_SUGGESTED_ADDRESS_TYPE,
        setSuggestedAddressModalVisible: SET_SUGGESTED_ADDRESS_MODAL_VISIBLE,
        setSuggestedAddressYesMethod: SET_SUGGESTED_ADDRESS_YES_METHOD,
      }),
      ...mapMutations('question', {
        hideQuestionModal: HIDE_QUESTION_MODAL,
        showQuestionModal: SHOW_QUESTION_MODAL,
      }),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      ...mapMutations('checkWireModal', {
        showCheckWireModal: SHOW_CHECK_WIRE_MODAL,
        setCreationStatus: SET_CREATION_SUCCEEDED,
      }),
      setBreadcrumbData(account, user) {
        this.breadcrumbData = [];
        this.breadcrumbData.push(
          Object.assign({}, {
            title: 'Search',
            goto: { name: 'Users' },
            selected: false,
          }),
        );
        const params = Object.assign({}, this.$route.params);
        let goto;
        if (user.userType === UserTypes.FINANCIAL_ADVISOR.name) {
          goto = { name: 'EditFinancialAdvisor', params: { name: params.name, userId: params.userId } };
        } else {
          goto = { name: 'UserDetail', params: { name: params.name, userId: params.userId } };
        }
        const title = user.fullName;
        this.breadcrumbData.push(
          Object.assign({}, {
            title,
            goto,
            selected: false,
          }),
        );

        const gotoAccount = { name: this.$route.name, params };
        this.breadcrumbData.push(
          Object.assign({}, {
            title: account.accountName,
            goto: gotoAccount,
            selected: true,
          }),
        );
      },
      showUpdateAccountConfirmationModal() {
        this.accountUsersLoading = false;
        this.showQuestionModal({
          title: 'Update Institution Account Information',
          question: 'Are you sure about these changes?',
          method: () => {
            this.methodToExecuteInUpdateAccountQuestionModal();
          },
          yesButtonText: Constants.purchaseNote.modalYesButtonText,
          hideCancelButton: false,
        });
      },
      async save(payload) {
        this.hideQuestionModal();
        this.accountUsersLoading = true;
        try {
          await this.editInstitutionAccountInformation({
            params: {
              accountId: this.accountId,
              organizationId: this.organization.id,
            },
            payload,
          });
          this.loadAccountInformation();
          this.accountUsersLoading = false;
        } catch (err) {
          this.accountUsersLoading = false;
        }
      },
      async methodToExecuteInUpdateAccountQuestionModal() {
        this.hideQuestionModal();
        await this.save({
          name: this.organization.name,
          type: camelToSnakeCase(this.organization.type).toUpperCase(),
          phoneNumber: this.removeFormattingCharacters(this.organization.phoneNumber),
          taxId: this.removeFormattingCharacters(this.organization.taxId),
          mailingAddress: this.organization.mailingAddress,
          mainAddress: this.organization.mainAddress,
        });
      },
      displayImpactPreferenceSuccessfulNotification() {
        this.preferencesSuccessMessage = Constants.adminPreferences.successMessage;
        this.isPreferencesSuccessMessageVisible = true;
        this.preferencesSpinner = false;
        this.goToSegment('preferences-segment');
      },
      displayUpcomingTransactionsWithActionAlert() {
        this.shouldUpcomingTransactionsWithActionsBeDisplayed = true;
        this.shouldUpcomingTransactionsWithNoActionsBeDisplayed = false;
      },
      displayUpcomingTransactionsWithNoActionAlert() {
        this.shouldUpcomingTransactionsWithActionsBeDisplayed = false;
        this.shouldUpcomingTransactionsWithNoActionsBeDisplayed = true;
      },
      goToSegment(segment) {
        document.getElementById(segment).scrollIntoView();
      },
      hideUpcomingTransactionsAlerts() {
        this.shouldUpcomingTransactionsWithActionsBeDisplayed = false;
        this.shouldUpcomingTransactionsWithNoActionsBeDisplayed = false;
      },
      setAdditionalEmailSpinnerOff() {
        this.isAdditionalEmailsLoading = false;
        this.loadAccountUsers();
      },
      setAdditionalEmailSpinnerOn() {
        this.isAdditionalEmailsLoading = true;
      },
      setFundingSourceSpinnerOff() {
        this.isFundingSourcesSegmentLoading = false;
      },
      setFundingSourceSpinnerOn() {
        this.isFundingSourcesSegmentLoading = true;
      },
      setNotesSpinnerOff() {
        this.isNotesSegmentLoading = false;
      },
      setNotesSpinnerOn() {
        this.isNotesSegmentLoading = true;
      },
      setParams() {
        this.userId = this.$route.params.userId;
        this.accountId = this.$route.params.accountId;
        this.name = this.$route.params.name;
      },
      setPreferencesSpinnerOff() {
        this.preferencesSpinner = false;
      },
      setPreferencesSpinnerOn() {
        this.preferencesSpinner = true;
      },
      setUpcomingTransactionsSpinnerOff() {
        this.isUpcomingTransactionsSegmentLoading = false;
      },
      setUpcomingTransactionsSpinnerOn() {
        this.isUpcomingTransactionsSegmentLoading = true;
      },
      setRepresentativesSpinnerOn() {
        this.isRepresentativesLoading = true;
      },
      setRepresentativesSpinnerOff() {
        this.isRepresentativesLoading = false;
      },
      hidePreferencesSuccessMessage() {
        this.isPreferencesSuccessMessageVisible = false;
      },
      loadAccountInformation() {
        this.loadUserDetail();
        this.loadAccountUsers();
      },
      loadUserDetail() {
        this.userDetailLoading = true;
        return this.getUserDetail(this.userId)
          .then((user) => {
            this.userDetailLoading = false;
            this.user = user;
            this.userAccount = this.findAccount(user.accounts, this.accountId);
            this.previousFundingSourceId = this.userAccount.defaultFundingSourceId;
          }).catch((err) => {
            this.userDetailLoading = false;
            if (err !== 'UNAUTHORIZED') {
              this.setErrorsModalInfo(['We could not retrieve user detail']);
              this.showErrorsModal();
            }
          });
      },
      removeFormattingCharacters(text) {
        return text.replace(/[-_() ]/g, '');
      },
      validateAndSubmitAccount() {
        this.accountUsersLoading = true;
        if (!this.$refs.addresses.isMailingAddressDifferent) {
          this.organization.mailingAddress = this.organization.mainAddress;
        }
        this.showUpdateAccountConfirmationModal();
      },
      showUserModalsError(listUserErrors) {
        this.setErrorsModalInfo(listUserErrors);
        this.showErrorsModal();
      },
      shouldSubmitButtonBeDisabled() {
        if (!this.organization.id) {
          return true;
        }

        const areBothAddressesValid = this.areMainAndMailingAddressValid();
        const isPhoneNumberValid = this.isPhoneNumberValid();
        const isTaxIdValid = this.isTaxIdValid();

        return !isStringValid(this.organization.name)
          || !isTaxIdValid
          || !isPhoneNumberValid
          || !isStringValid(this.organization.type)
          || !areBothAddressesValid;
      },
      areMainAndMailingAddressValid() {
        let areBothAddressesValid = isAddressValid(this.organization.mainAddress);

        if (this.$refs.addresses && this.$refs.addresses.isMailingAddressDifferent) {
          areBothAddressesValid = isAddressValid(this.organization.mailingAddress);
        }

        return areBothAddressesValid;
      },
      isTaxIdValid() {
        let isTaxIdValid = isStringValid(this.organization.taxId);

        if (isTaxIdValid) {
          isTaxIdValid = (this.removeFormattingCharacters(this.organization.taxId).length === 9);
        }

        return isTaxIdValid;
      },
      isPhoneNumberValid() {
        let isPhoneNumberValid = isStringValid(this.organization.phoneNumber);

        if (isPhoneNumberValid) {
          isPhoneNumberValid = (
            this.removeFormattingCharacters(this.organization.phoneNumber).length === 10);
        }

        return isPhoneNumberValid;
      },
      truncateAddressIfNecessary(address) {
        const localAddress = { ...address };
        const hasStreetAddress1 = Object.prototype.hasOwnProperty.call(localAddress, 'streetAddress1');
        const hasStreetAddress2 = Object.prototype.hasOwnProperty.call(localAddress, 'streetAddress2');
        if (hasStreetAddress1 && hasStreetAddress2) {
          const totalAdressLength = address.streetAddress1.length + address.streetAddress2.length;
          if (totalAdressLength > 50) {
            const charsToCut = totalAdressLength - 50;
            localAddress.streetAddress2 = address.streetAddress2.slice(0, -charsToCut);
            return localAddress;
          }
        }
        return address;
      },
      async loadAccountUsers() {
        try {
          const [
            account,
            accountOrganizations,
            accountRepresentatives,
            accountEmails,
          ] = await Promise.all([
            this.getAccount(this.accountId),
            this.getAccountOrganizations(this.accountId),
            this.getAccountUsers(this.accountId),
            this.getAccountEmails(this.accountId),
          ]);
          this.account = account;
          this.accountEmails = accountEmails;
          this.accountRepresentatives = accountRepresentatives;
          this.organization = Object.assign({}, accountOrganizations[0]);
          this.previousPaymentPreference = this.account.paymentPreference;
          return Promise.resolve();
        } catch (err) {
          if (err !== 'UNAUTHORIZED') {
            this.setErrorsModalInfo(['We could not retrieve users list']);
            this.showErrorsModal();
            return err;
          }
          return Promise.reject();
        } finally {
          this.accountUsersLoading = false;
        }
      },
      loadNotesList() {
        this.noteLoading = true;
        return this.getAccountNotes(this.accountId)
        .then((notes) => {
          this.noteLoading = false;
          this.notes = notes;
          const openNotes = _.filter(notes, { status: 'open' });
          this.currentAmountBalance = _.sumBy(['currentBalance'], _.partial(_.sumBy, openNotes));
        })
        .catch((err) => {
          this.noteLoading = false;
          if (err !== 'UNAUTHORIZED') {
            this.setErrorsModalInfo(['We could not retrieve notes list']);
            this.showErrorsModal();
          }
        });
      },
      loadFundingSourceAfterModalSave() {
        if (this.creationSucceeded) {
          this.loadUserDetail();
          this.loadFundingSources();
          this.setCreationStatus(false);
        }
      },
      loadFundingSources() {
        this.fundingLoading = true;
        return this.getAccountFundingSources(this.accountId)
        .then((data) => {
          this.fundingLoading = false;
          this.fundingSources = data.filter(fss => !fss.removed &&
          (fss.type === 'bank' ||
          fss.type === 'check' ||
          fss.type === 'wire/ach'));
        }).catch((err) => {
          this.fundingLoading = false;
          if (err !== 'UNAUTHORIZED') {
            this.setErrorsModalInfo(['We could not retrieve bank accounts list']);
            this.showErrorsModal();
          }
        });
      },
      getAccountType() {
        if (this.userAccount && this.userAccount.accountType) {
          const accountType = this.userAccount.accountType;
          return `${accountType[0]}${accountType.slice(1).toLowerCase()}`;
        }
        return '';
      },
      showDefaultFundingSourceConfirmationModal({
        fundingSource,
      }) {
        this.userAccount.defaultFundingSourceId = this.previousFundingSourceId;
        this.showQuestionModal({
          title: 'Bank account',
          question: `Are you sure you want to select ${fundingSource.name} as new default bank account?`,
          method: () => {
            this.methodToExecuteInDefaultFundingSourceQuestionModal(fundingSource.id);
          },
          yesButtonText: Constants.purchaseNote.modalYesButtonText,
          hideCancelButton: false,
        });
      },
      showConfirmationModal(paymentPreference) {
        this.account.paymentPreference = this.previousPaymentPreference;
        this.showQuestionModal({
          title: Constants.accountDetail.modalTitle,
          question: `Are you sure you want to select ${this.formatPaymentPreference(paymentPreference)} as your new default payment action?`,
          method: () => {
            this.methodToExecuteInQuestionModal(paymentPreference);
          },
          yesButtonText: Constants.accountDetail.modalYesButtonText,
          hideCancelButton: false,
        });
      },
      methodToExecuteInDefaultFundingSourceQuestionModal(fundingSourceId) {
        this.hideQuestionModal();
        const account = this.userAccount;
        this.setDefaultFundingSource({
          accountId: account.id,
          fundingSourceId,
        }).then(() => {
          this.previousFundingSourceId = fundingSourceId;
          this.userAccount.defaultFundingSourceId = fundingSourceId;
        });
      },
      methodToExecuteInQuestionModal(paymentPreference) {
        this.hideQuestionModal();
        this.updateAccountPaymentPreference({
          accountId: this.accountId,
          paymentPreference,
        }).then(() => {
          this.previousPaymentPreference = paymentPreference;
          this.account.paymentPreference = paymentPreference;
        });
      },
      openCheckWireModal() {
        const filteredFundingSources = this.fundingSources.filter(fss => fss.type === 'check' ||
          fss.type === 'wire-transfer');
        this.showCheckWireModal({ accountId: this.accountId,
          fundingSources: filteredFundingSources });
      },
      goToPurchaseInvestmentNote() {
        const accountId = this.accountId;
        const userId = this.userId;
        const name = this.name;
        this.$router.push({ name: 'PurchaseInvestmentNote', params: { accountId, userId, name } });
      },
      goToViewDetail(note) {
        const userId = this.userId;
        const name = this.name;
        this.$router.push({ name: 'NoteDetail', params: { name, userId, noteId: note.id } });
      },
      goBack() {
        this.$router.push({ name: 'Users', params: { name: this.name } });
      },
      onInstitutionNameUpdate(value) {
        if (value) {
          this.organization.name = value.trim();
        }
      },
      async handleDefaultFundingSourceUpdated() {
        await Promise.all([
          this.loadUserDetail(),
          this.loadAccountUsers(),
        ]);
        await this.$nextTick();
        if (this.$refs.upcomingTransactions) {
          await this.$refs.upcomingTransactions.initializeState();
        }
      },
      showBankAccountChangedWarning() {
        this.isBankAccountChangedWarningVisible = true;
      },
      hideBankAccountChangedWarning() {
        this.isBankAccountChangedWarningVisible = false;
      },
      handleGoToUpcomingTransactions() {
        this.goToSegment('upcoming-transactions-segment');
      },
    },
  };
</script>

<style scoped>
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  .zero-margin-x {
    margin-left: 0;
    margin-right: 0;
  }
  .left-margin {
    margin-left: 2%;
  }
  .right-margin {
    margin-left: -4%;
  }
  .top-margin {
    margin-top: 2%;
  }
  .space {
    margin-top: 4%;
  }
  .content-heading {
    font-weight: normal;
  }
  .panel-title {
    font-size: 20px;
    margin-bottom: 5px;
  }
  .panel-subtitle {
    font-size: 16px;
  }
  .panel-default {
    padding: 30px 40px;
  }
  .panel-heading {
    background-color: #ffffff;
    font-size: 16px;
    padding: 0px 5px;
  }
  label {
    color: #888;
    font-size: 14px;
    width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  table {
    border-collapse: collapse;
  }
  th {
    padding: 14px 8px;
    color: #888;
    border-bottom-width: 1px;
    vertical-align: bottom;
    border-bottom: 1px solid #eee;
    border-top: 0;
    line-height: 1.52857143;
    font-weight: normal;
  }
  td {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  .table-striped>tbody>tr:nth-of-type(odd) {
    background-color: #fafbfc;
  }
  .amountBalance {
    font-size: 20px;
  }
  .label {
    display: inline-block;
    padding: 4px 10px;
  }
  .label-name {
    color: #31b0d5;
  }
  .label-names {
    font-size: 20px;
  }
  .rows-9 thead th {
    width: 11.1%;
  }
  .rows-5 thead th {
    width: 25%;
  }
  .btn-back {
    color: #31b0d5;
    background-color: Transparent;
    background-repeat:no-repeat;
    border: none;
    cursor:pointer;
    overflow: hidden;
    outline:none;
    padding-left: 20px;
  }
  .icon-back {
    position:relative;
  }
  .icon-back:before {
    content: "\f060";
    font-family: FontAwesome;
    position:absolute;
    left: 0px;
  }
  .button-check-wire {
    background-color: white;
    border-color: #127285;
    color: #127285;
  }
  .capitalize-text {
    text-transform: capitalize;
  }
  @media only screen and (max-width: 1440px) {
    .table-fixed-header--md tr td:last-child {
      padding-right: 40px;
    }
    .table-fixed-header--md tr th:last-child {
      padding-right: 120px;
    }
  }
  .bank-account__radio-button {
    display: inline-block;
    margin-bottom: 0!important;
  }
</style>



// WEBPACK FOOTER //
// EditInstitutionAccount.vue?eb3bf5e8